import { Link } from "react-router-dom";
import { PageMeta } from "@/components/PageMeta";
import { ContentPageShell, Prose } from "@/components/ContentPageShell";

export default function HowItWorks() {
  return (
    <>
      <PageMeta
        title="Jak Tipomatic funguje — návod krok za krokem"
        description="Detailní návod: založení účtu, vytvoření tipovací ligy, pozvání kamarádů, tipování zápasů, bodování a vyhlášení vítěze."
        path="/how-it-works"
      />
      <ContentPageShell>
        <h1 className="text-3xl md:text-4xl font-bold mb-6">Jak Tipomatic funguje</h1>
        <Prose>
          <p>
            Tipomatic je stavěný tak, aby se v něm zorientoval každý — od fanouška, který nikdy netipoval, až po
            hardcore statistika. Celý proces od registrace k prvnímu tipu zabere pod pět minut. Tato stránka
            projde všechny kroky detailně, takže vás v aplikaci nic nepřekvapí.
          </p>

          <h2>1. Registrace účtu</h2>
          <p>
            Přejděte na <Link to="/auth">přihlašovací stránku</Link> a vytvořte si účet. Podporujeme přihlášení
            e-mailem s heslem i přes Google (jedním kliknutím). Po registraci vám na e-mail dorazí potvrzovací
            odkaz — otevřete ho a účet je aktivní. Žádné placené plány, žádné vyžadování platební karty.
          </p>
          <p>
            V profilu (dostupný po přihlášení) můžete kdykoli změnit jméno, heslo, jazyk aplikace (CS/EN) a
            spravovat notifikace.
          </p>

          <h2>2. Vytvoření tipovací ligy</h2>
          <p>
            Po přihlášení uvidíte přehled svých lig. Kliknete na „Vytvořit ligu“, pojmenujete ji (např.
            „Hospoda U Vencla — Liga mistrů“) a vyberete turnaj, který chcete tipovat. V lize se zobrazí jeho
            připravený program zápasů.
          </p>
          <p>Jako zakladatel ligy jste zároveň jejím <strong>adminem</strong> — můžete:</p>
          <ul>
            <li>upravovat bodovací pravidla (viz <Link to="/scoring-rules">Bodování</Link>);</li>
            <li>upravovat popis a název ligy (podporujeme jednoduché HTML formátování);</li>
            <li>schvalovat nebo odmítat žádosti o vstup;</li>
            <li>odebírat hráče, kteří ligu opustili;</li>
            <li>zapnout Speciální tipy a nastavit body za správné odpovědi.</li>
          </ul>

          <h2>3. Pozvání kamarádů</h2>
          <p>
            Každá liga má unikátní pozvánkový odkaz (formát{" "}
            <code className="rounded bg-white/10 px-1.5 py-0.5 text-sm">tipomatic.app/invite/ABC123</code>).
            Zkopírujete ho z detailu ligy a pošlete komukoli — do skupinového chatu, e-mailem nebo přes SMS.
            Když příjemce odkaz otevře, přihlásí se nebo registruje a je automaticky přidán jako člen.
          </p>
          <p>
            Pozvánkové odkazy je možné kdykoli vygenerovat znovu (starý odkaz přestane platit), což se hodí když
            se odkaz omylem dostane do nesprávných rukou.
          </p>

          <h2>4. Tipování zápasů</h2>
          <p>
            Před turnajem doporučujeme <strong>bulk tipování</strong> — otevřete si obrazovku "Natipovat vše" a
            vyplníte předpokládaný výsledek u všech zápasů skupinové fáze najednou. Ušetří vám to spoustu klikání.
            Jednotlivé tipy pak můžete upravovat až do vteřiny před výkopem konkrétního zápasu.
          </p>
          <p>
            Tipujete <strong>přesný výsledek</strong> (např. Česko 2:1 Chorvatsko). Aplikace pak vyhodnotí jak
            přesně jste zápas trefili — plný počet bodů za přesný výsledek, částečný za správnou tendenci nebo
            správný rozdíl gólů (přesná pravidla nastavuje admin ligy).
          </p>
          <p>
            Po výkopu se tip <strong>uzamkne</strong> a už s ním nelze hnout. Toto je vědomé rozhodnutí — zabraňuje
            to sporům a nefér chování. Pokud jste zapomněli natipovat, obdržíte za daný zápas 0 bodů.
          </p>

          <h2>5. Speciální tipy</h2>
          <p>
            Pokud je admin v lize zapne, objeví se navíc záložka <strong>Speciální tipy</strong>. Tipujete v ní
            otázky mimo jednotlivé zápasy — například nejlepší a nejhorší tým ligové fáze, tým s nejvíce
            vstřelenými nebo obdrženými góly, největší rozdíl ve skóre nebo nejvyšší počet gólů v jednom zápase.
            Odpovídáte výběrem týmu nebo číslem.
          </p>
          <p>
            Speciální tipy se uzavírají výkopem prvního zápasu turnaje (případně dřívějším termínem, který zadá
            správce). Po zveřejnění správné odpovědi se body automaticky přičtou do celkového součtu v tabulce —
            kolik bodů otázka má, si může admin ligy nastavit sám.
          </p>

          <h2>6. Bodování a žebříček</h2>
          <p>
            Po zadání oficiálního výsledku Tipomatic přepočítá body všem hráčům v lize. Žebříček se aktualizuje
            průběžně. Podrobnosti o jednotlivých
            typech bodů najdete v sekci <Link to="/scoring-rules">Bodovací pravidla</Link>.
          </p>
          <p>
            V ligovém detailu vidíte:
          </p>
          <ul>
            <li>aktuální pořadí a body všech hráčů,</li>
            <li>historii svých tipů se získanými body u každého zápasu,</li>
            <li>tipy ostatních hráčů (odemknou se až po výkopu — nikdo tedy nemůže opisovat),</li>
            <li>rozdíl bodů oproti hráči přede vás — motivační ukazatel pro každý zbývající zápas.</li>
          </ul>

          <h2>7. Vyhlášení vítěze</h2>
          <p>
            Po posledním zápase turnaje se žebříček uzamkne a admin ligy může výsledky zveřejnit (např. sdílet
            screenshotem do skupinového chatu). O tom, co vítěz dostane, si rozhodujete sami — někde je to lahev
            vína, jinde závazek nezakládat další ligu bez pozvání předchozího vítěze. Tipomatic žádné odměny
            nezprostředkovává.
          </p>

          <h2>Instalace jako aplikace (PWA)</h2>
          <p>
            Tipomatic je progresivní webová aplikace — nemusíte ho stahovat z App Store ani Google Play.
            V mobilním prohlížeči (Safari na iOS, Chrome na Androidu) najdete v menu volbu "Přidat na plochu" nebo
            "Nainstalovat aplikaci". Detailní postup i s videem najdete v <Link to="/help">nápovědě</Link>.
          </p>

          <h2>Máte otázku, kterou jsme nezodpověděli?</h2>
          <p>
            Napište nám na <Link to="/contact">kontaktní stránce</Link> — dotaz zapracujeme buď do této nápovědy,
            nebo do aplikace samotné.
          </p>
        </Prose>
      </ContentPageShell>
    </>
  );
}
